/**
 * Passport.js
 *
 * A guest's honorary passport from Flight YJ030, a souvenir after the party (/fly/souvenir).
 * Built from Passport_Handoff/YashJain_Passport.html: a cover in the guest's colour and a data page,
 * filled from their booking and their country (passport-countries.json, sent by the backend).
 *
 * Drawn at a fixed size and scaled to fit, like the boarding pass:
 *   book     -   1600×1200, cover and data page side by side (wide screens, and the PNG/PDF)
 *   stacked  -   700×1960, cover above the data page (phones: the book would be far too small to read)
 */

import React, { useLayoutEffect, useState } from "react";

const INK = "#1B2A41";
const MUTED = "#5B6577";
const PAPER_PAGE = "#F6F1E4";
const BACKDROP = "#E3DCCF";
const GOLD = "#D4AF5A";
const CREAM = "#FBF7EF";
const STAMP_RED = "#B5192E";

const MONO = "'JetBrains Mono', monospace";
const SANS = "'Space Grotesk', system-ui, sans-serif";
const SERIF = "'Cinzel', Georgia, serif";
// Thai, Arabic and other scripts fall back to the phone's own fonts
const SERIF_ANY = "'Cinzel', 'Noto Serif', 'Noto Serif Thai', 'Noto Naskh Arabic', 'FreeSerif', serif";

const PAGE_WIDTH = 700;
const PAGE_HEIGHT = 980;
export const BOOK = { width: 1600, height: 1200 };
export const STACKED = { width: PAGE_WIDTH, height: PAGE_HEIGHT * 2 };

// Screens narrower than this get the stacked passport
const MOBILE_BREAKPOINT = 640;

const MRZ_LENGTH = 44;

// ---------------- Colour ---------------- //

const luminance = (hex) => {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
        .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrast = (a, b) => {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
};

/** Gold lettering on the cover, unless it's too faint on the guest's colour (WCAG 4.5:1), then cream */
export const coverInk = (colour) => (contrast(GOLD, colour) >= 4.5 ? GOLD : CREAM);

// ---------------- Text ---------------- //

const splitName = (fullName) => {
    const words = fullName.trim().toUpperCase().split(/\s+/);
    return { surname: words[words.length - 1], given: words.slice(0, -1).join(" ") || words[0] };
};

const mrzText = (text) => text.toUpperCase().normalize("NFKD").replace(/[^A-Z0-9]+/g, "<");

/** The two machine-readable lines, in the design's style (not a real ICAO checksum) */
export const machineLines = (fullName, pnr, nationality) => {
    const { surname, given } = splitName(fullName);
    const line1 = `P<BAH<${mrzText(surname)}<<${mrzText(given)}`.padEnd(MRZ_LENGTH, "<").slice(0, MRZ_LENGTH);
    const line2 = `${pnr}<<HONORARY<${mrzText(nationality || "")}<<VALID<30OCT26`;
    return [line1, line2];
};

// ---------------- Pieces ---------------- //

const label = { fontSize: 11, fontWeight: 700, letterSpacing: ".16em", color: MUTED };

const Field = ({ name, children, style }) => (
    <div>
        <div style={label}>{name}</div>
        <div style={{ fontSize: 20, fontWeight: 700, ...style }}>{children}</div>
    </div>
);

/** A head-and-shoulders outline, for guests without a photo */
const Silhouette = () => (
    <svg width="100%" height="100%" viewBox="0 0 190 240" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
        <circle cx="95" cy="92" r="44" fill="#BDB4A1" />
        <path d="M14 240c4-52 38-82 81-82s77 30 81 82z" fill="#BDB4A1" />
    </svg>
);

/** The city in the cover's seal, squeezed to fit if it's long */
const SealCity = ({ city, ink }) => {
    const text = city.toUpperCase();
    const long = text.length > 11;
    return (
        <text x="100" y="108" textAnchor="middle" fontFamily="Cinzel, Georgia, serif" fontSize="15" fontWeight="700" letterSpacing="2" fill={ink}
            {...(long ? { textLength: 136, lengthAdjust: "spacingAndGlyphs" } : {})}>
            {text}
        </text>
    );
};

const Cover = ({ colour, country, city, rounded }) => {
    const ink = coverInk(colour);
    const local = country.local_name || country.country || "";
    const english = country.english_name || "";
    return (
        <div style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT, boxSizing: "border-box", background: colour, borderRadius: rounded, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", padding: "88px 60px 64px", color: ink, overflow: "hidden", textAlign: "center" }}>
            <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 40, background: "linear-gradient(90deg,rgba(0,0,0,0),rgba(0,0,0,.3))" }} />
            <div style={{ fontFamily: SERIF_ANY, fontSize: 30, fontWeight: 700, letterSpacing: "0.18em", lineHeight: 1.25 }}>{local}</div>
            {english && english !== local && (
                <div style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 600, letterSpacing: "0.18em", marginTop: 8, opacity: 0.92 }}>{english}</div>
            )}

            <svg width="230" height="230" viewBox="0 0 200 200" style={{ marginTop: english ? 40 : 64 }} aria-hidden="true">
                <circle cx="100" cy="100" r="92" fill="none" stroke={ink} strokeWidth="3" />
                <circle cx="100" cy="100" r="80" fill="none" stroke={ink} strokeWidth="1.5" strokeDasharray="2 5" />
                <path d="M100 44l7 15 16 2-12 11 3 16-14-8-14 8 3-16-12-11 16-2z" fill="none" stroke={ink} strokeWidth="3" strokeLinejoin="round" />
                <path d="M58 112c8 22 24 34 42 34s34-12 42-34" fill="none" stroke={ink} strokeWidth="3" strokeLinecap="round" />
                <path d="M60 124l-9 3M66 134l-8 6M75 142l-6 7M140 124l9 3M134 134l8 6M125 142l6 7" stroke={ink} strokeWidth="3" strokeLinecap="round" />
                <SealCity city={city} ink={ink} />
                <text x="100" y="176" textAnchor="middle" fontFamily="Cinzel, Georgia, serif" fontSize="20" fontWeight="700" fill={ink}>26</text>
            </svg>
            <div style={{ fontFamily: SERIF_ANY, fontSize: 64, fontWeight: 700, letterSpacing: "0.14em", marginTop: 62, lineHeight: 1.15 }}>{country.passport_word || "PASSPORT"}</div>

            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
                <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 600, letterSpacing: "0.3em", borderTop: `1.5px solid ${ink}`, borderBottom: `1.5px solid ${ink}`, padding: "10px 4px" }}>HONORARY · ONE NIGHT ONLY</div>
                <svg width="60" height="38" viewBox="0 0 64 40" aria-hidden="true">
                    <rect x="2" y="2" width="60" height="36" rx="6" fill="none" stroke={ink} strokeWidth="3" />
                    <circle cx="32" cy="20" r="8" fill="none" stroke={ink} strokeWidth="3" />
                    <path d="M2 20h22M40 20h22" stroke={ink} strokeWidth="3" />
                </svg>
            </div>
        </div>
    );
};

const DataPage = ({ pass, country, city, photoUrl, rounded }) => {
    const colour = pass.colour;
    const { surname, given } = splitName(pass.fullName);
    const nationality = (country.nationality || "").toUpperCase();
    const flag = country.flag_colours?.length ? country.flag_colours : [colour];
    const [line1, line2] = machineLines(pass.fullName, pass.pnr, nationality);

    return (
        <div style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT, boxSizing: "border-box", background: PAPER_PAGE, borderRadius: rounded, position: "relative", overflow: "hidden", color: INK, textAlign: "left" }}>
            <div style={{ position: "absolute", inset: 0, background: `repeating-radial-gradient(circle at 70% 38%,${colour}14 0 1px,transparent 1px 14px)` }} />
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 34, background: "linear-gradient(90deg,rgba(0,0,0,.16),rgba(0,0,0,0))" }} />
            <div style={{ position: "absolute", right: -30, top: 330, transform: "rotate(-24deg)", fontFamily: SERIF_ANY, fontSize: 120, fontWeight: 700, color: colour, opacity: 0.06, whiteSpace: "nowrap" }}>{country.local_name}</div>

            <div style={{ position: "relative", padding: "52px 56px 0 64px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, fontWeight: 700, letterSpacing: ".2em", color: colour }}>
                    <span>PASSPORT</span>
                    <span>TYPE P · {nationality}</span>
                </div>
                <div style={{ display: "flex", height: 8, marginTop: 10, borderRadius: 2, overflow: "hidden", boxShadow: "0 0 0 1px rgba(27,42,65,.18)" }}>
                    {flag.map((c, i) => <div key={i} style={{ flex: 1, background: c }} />)}
                </div>

                <div style={{ display: "flex", gap: 30, marginTop: 30 }}>
                    <div style={{ flexShrink: 0, width: 190, height: 240, border: `2px solid ${INK}`, borderRadius: 6, background: "#E9E2D2", display: "flex", alignItems: "flex-end", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                        {photoUrl
                            ? <img src={photoUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 50%", filter: "grayscale(.15) contrast(1.02)" }} />
                            : <Silhouette />}
                    </div>
                    <div style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 13 }}>
                        <Field name="PASSPORT NO." style={{ fontFamily: MONO, fontSize: 24, color: colour }}>{pass.pnr}</Field>
                        <Field name="SURNAME" style={{ fontSize: 26 }}>{surname}</Field>
                        <Field name="GIVEN NAMES" style={{ fontSize: 26 }}>{given}</Field>
                        <Field name="NATIONALITY">{nationality} <span style={{ fontWeight: 500, color: MUTED }}>(HONORARY)</span></Field>
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: "16px 24px", marginTop: 30 }}>
                    <Field name="PLACE OF ISSUE">{city.toUpperCase()}</Field>
                    <Field name="AUTHORITY">RIGHT BROTHER CONSULATE</Field>
                    <Field name="DATE OF ISSUE" style={{ fontFamily: MONO }}>28 OCT 2026</Field>
                    <Field name="DATE OF EXPIRY" style={{ fontFamily: MONO, color: colour }}>30 OCT 2026</Field>
                </div>

                <div style={{ ...label, marginTop: 28 }}>OBSERVATIONS</div>
                <div style={{ marginTop: 8, fontSize: 19, lineHeight: 1.45, maxWidth: 400 }}>
                    <strong style={{ color: colour, fontFamily: `${SANS}, 'Noto Sans Thai', 'Noto Naskh Arabic'` }}>{country.welcome || "Welcome"}</strong>{" "}
                    Holder is entitled to food, drinks and questionable dance moves. Not valid for leaving early.
                </div>
            </div>

            <div style={{ position: "absolute", right: 50, top: 560, width: 168, height: 168, border: `4px double ${STAMP_RED}`, borderRadius: "50%", color: STAMP_RED, transform: "rotate(-16deg)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", opacity: 0.88 }}>
                <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".16em" }}>ENTRY ONLY</div>
                <div style={{ fontFamily: MONO, fontSize: 23, fontWeight: 700, lineHeight: 1.15, margin: "4px 0" }}>WITH<br />MOBILE PASS</div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".16em" }}>30.10.26</div>
            </div>

            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, background: "#ECE5D6", borderTop: "1px solid #D6CDB9", padding: "16px 30px 18px 64px", fontFamily: MONO, fontSize: 20, fontWeight: 500, letterSpacing: ".06em", lineHeight: 1.45, color: INK, whiteSpace: "nowrap", overflow: "hidden" }}>
                {line1}<br />{line2}
            </div>
        </div>
    );
};

/**
 * The passport at full size (no scaling)
 * @param {Object} pass         -   { pnr, fullName, colour }
 * @param {Object} passport     -   { country, city, local_name, english_name, passport_word, nationality, flag_colours, welcome }
 * @param {String} photoUrl     -   The guest's photo (a data: URL), or null for the silhouette
 * @param {Boolean} stacked     -   Cover above the data page, instead of side by side
 * @param {String} backdrop     -   Behind the passport: its own colour for downloads, transparent on the page
 */
export const PassportDrawing = ({ pass, passport, photoUrl, stacked, backdrop = BACKDROP }) => {
    const size = stacked ? STACKED : BOOK;
    const shadow = "0 40px 70px -30px rgba(40,30,20,.55),0 8px 18px rgba(40,30,20,.18)";
    return (
        <div style={{ width: size.width, height: size.height, position: "relative", overflow: "hidden", background: backdrop, fontFamily: SANS }}>
            {stacked ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 0, boxShadow: shadow, borderRadius: 18 }}>
                    <Cover colour={pass.colour} country={passport} city={passport.city} rounded="18px 18px 4px 4px" />
                    <DataPage pass={pass} country={passport} city={passport.city} photoUrl={photoUrl} rounded="4px 4px 18px 18px" />
                </div>
            ) : (
                <div style={{ position: "absolute", left: 100, top: 110, width: 1400, height: 980, display: "flex", borderRadius: 18, boxShadow: shadow }}>
                    <Cover colour={pass.colour} country={passport} city={passport.city} rounded="18px 4px 4px 18px" />
                    <DataPage pass={pass} country={passport} city={passport.city} photoUrl={photoUrl} rounded="4px 18px 18px 4px" />
                </div>
            )}
        </div>
    );
};

/**
 * Which passport fits the screen, and how much to shrink it
 * @returns {Object}    -   { stacked, scale (at most 1) }
 */
export const usePassportLayout = () => {
    const [layout, setLayout] = useState({ stacked: false, scale: 1 });
    useLayoutEffect(() => {
        const fit = () => {
            const stacked = window.innerWidth < MOBILE_BREAKPOINT;
            const width = (stacked ? STACKED : BOOK).width;
            setLayout({ stacked, scale: Math.min(window.innerWidth - 16, stacked ? 560 : 1100) / width });
        };
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, []);
    return layout;
};

/**
 * The passport, scaled to fit the screen
 */
const Passport = ({ pass, passport, photoUrl, layout }) => {
    const size = layout.stacked ? STACKED : BOOK;
    const { scale } = layout;
    return (
        <div style={{ width: "100%", overflow: "hidden", display: "flex", justifyContent: "center", height: size.height * scale }}>
            <div style={{ width: size.width, height: size.height, transformOrigin: "top left", flexShrink: 0, transform: `scale(${scale})`, marginRight: size.width * scale - size.width }}>
                <PassportDrawing pass={pass} passport={passport} photoUrl={photoUrl} stacked={layout.stacked} backdrop="transparent" />
            </div>
        </div>
    );
};

export default Passport;
